import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import plus from '@/assets/images/plus.svg';
import { useCurrency } from '@/context/CurrencyContext/CurrencyContext';
import { useMenuView } from '@/context/MenuViewContext/MenuViewContext';
import { useDigitalOrderingEnabled } from '@/hooks/useDigitalOrderingEnabled.ts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import useValidateQueryParams from '@/hooks/useValidateQueryParams.tsx';
import { Position } from '@/interfaces/Menu';
import { useBranding } from '@/utils/branding';
import ItemDetails from '../ItemDetails/ItemDetails';
import styles from './ItemCard.module.scss';
import ItemCardCashbackPill from './ItemCardCashbackPill';

interface ItemCardProps {
  position: Position;
  menuId: string;
  toggleCashbackBanner: () => void;
  onAddToCart?: () => void; // Callback for add button click
}

export const ItemCard = ({
  position,
  menuId,
  toggleCashbackBanner,
  onAddToCart,
}: ItemCardProps) => {
  const {
    state: { isGrid },
  } = useMenuView();
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const [isItemDetailsOpen, setIsItemDetailsOpen] = useState(false);
  const shouldShowCashbackPill = useFeatureFlag('show_cashback_pill');
  const { showCashbackUI } = useBranding();
  const isAvailable = position.availability.status === 'AVAILABLE';
  const location = useLocation();
  const navigate = useNavigate();
  const toggleCashbackBannerRef = useRef(toggleCashbackBanner);
  const { queryParams } = useValidateQueryParams();
  const { outletId, brandId } = queryParams();
  const { data: isDigitalOrderingEnabled } = useDigitalOrderingEnabled(
    brandId ?? undefined,
    outletId ?? undefined
  );

  // Update ref when prop changes
  useEffect(() => {
    toggleCashbackBannerRef.current = toggleCashbackBanner;
  }, [toggleCashbackBanner]);

  // Check if modal should be open based on URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const itemId = searchParams.get('itemId');
    if (itemId === position.id && !isItemDetailsOpen) {
      setIsItemDetailsOpen(true);
      toggleCashbackBannerRef.current();
    } else if (!itemId && isItemDetailsOpen) {
      setIsItemDetailsOpen(false);
      toggleCashbackBannerRef.current();
    }
  }, [location.search, position.id, isItemDetailsOpen]);

  const handleItemClick = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('itemId', position.id);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const handleCloseModal = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('itemId');
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  // Handle add button click - prevent propagation to avoid opening item details
  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToCart?.();
  };

  return (
    <>
      <div
        className={clsx(styles.root, {
          [styles.unavailable]: position.availability.status !== 'AVAILABLE',
          [styles.gridView]: isGrid,
        })}
        data-testid="item-card"
        onClick={handleItemClick}
      >
        <div className={styles.itemDetails}>
          <div className={styles.details}>
            <h2 data-testid="item-name">{position.name}</h2>
            <p data-testid="item-description">{position.description}</p>
          </div>
          <div className={styles.price}>
            <p
              className={clsx(styles.priceValue, {
                [styles.strikethrough]: position.availability.status !== 'AVAILABLE',
              })}
              data-testid="item-price"
            >
              {currencySymbol}{' '}
              <span data-testid="item-price-value">{position.price.gross.toFixed(2)}</span>
            </p>
            {showCashbackUI && shouldShowCashbackPill && position.cashbackValue && (
              <ItemCardCashbackPill cashbackAmount={position.cashbackValue.cash || 0} />
            )}
          </div>
        </div>

        <div className={styles.image}>
          <img data-testid="item-image" src={position.imageUris['720_720']} alt={position.name} />
          {isAvailable && onAddToCart && isDigitalOrderingEnabled && (
            <button
              className={styles.addButton}
              onClick={handleAddClick}
              aria-label={t('menu.addToCart')}
              data-testid="add-to-cart-button"
            >
              <img src={plus} alt="Plus" />
            </button>
          )}
        </div>
      </div>
      {isItemDetailsOpen && (
        <ItemDetails
          position={position}
          menuId={menuId}
          onClose={handleCloseModal}
          isAvailable={isAvailable}
        />
      )}
    </>
  );
};
